import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, Typography, InputAdornment, CircularProgress } from '@mui/material';
import { BusinessOutlined, PersonOutline, PhoneOutlined, EmailOutlined } from '@mui/icons-material';
import { useUpsertVendorMutation } from '../../../../services/VendorService';
import { VendorView } from '../../../../services/VendorService';

interface Props {
  open: boolean;
  onClose: () => void;
  onVendorCreated: (vendorId: string) => void;
}

const emptyVendor: VendorView = {
  id: '',
  vendorNumber: '',
  companyName: '',
  dba: '',
  status: 'ACTIVE',
  statusName: 'Active',
  createdDate: new Date().toISOString(),
  modifiedDate: new Date().toISOString(),
  primaryContactName: '',
  primaryPhone: '',
  primaryEmail: '',
  secondaryContactName: '',
  secondaryPhone: '',
  secondaryEmail: '',
  website: '',
  street: '',
  city: '',
  state: '',
  zip: '',
};

/**
 * VendorQuickAddDialog - Lightweight dialog for quickly creating a new vendor
 * from within FM or PM ticket forms. Captures only essential fields.
 */
export const VendorQuickAddDialog: React.FC<Props> = ({ open, onClose, onVendorCreated }) => {
  const [vendor, setVendor] = useState<VendorView>({ ...emptyVendor });
  const [upsertVendor, { isLoading }] = useUpsertVendorMutation();

  const handleSave = async () => {
    try {
      const result = await upsertVendor({
        vendorView: vendor,
        modifiedBy: 'system',
      }).unwrap();
      onVendorCreated(result.id);
      setVendor({ ...emptyVendor });
      onClose();
    } catch (error) {
      console.error('Failed to create vendor:', error);
    }
  };

  const handleClose = () => {
    setVendor({ ...emptyVendor });
    onClose();
  };

  const updateField = (field: keyof VendorView, value: string) => {
    setVendor((prev: VendorView) => ({ ...prev, [field]: value }));
  };

  const isValid = vendor.companyName.trim().length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ borderBottom: '2px solid #3b82f6', pb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Quick Add Vendor
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
          Create a new vendor with basic information. Full details can be added later.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Company Name"
            value={vendor.companyName}
            onChange={(e) => updateField('companyName', e.target.value)}
            size="small"
            fullWidth
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessOutlined sx={{ color: '#64748b', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Primary Contact Name"
            value={vendor.primaryContactName}
            onChange={(e) => updateField('primaryContactName', e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutline sx={{ color: '#64748b', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Primary Phone"
              value={vendor.primaryPhone}
              onChange={(e) => updateField('primaryPhone', e.target.value)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneOutlined sx={{ color: '#64748b', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Primary Email"
              value={vendor.primaryEmail}
              onChange={(e) => updateField('primaryEmail', e.target.value)}
              size="small"
              fullWidth
              type="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: '#64748b', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid || isLoading}
          sx={{
            backgroundColor: '#162346',
            '&:hover': { backgroundColor: '#1a2a52' },
            fontWeight: 600,
          }}
        >
          {isLoading ? <CircularProgress size={20} /> : 'Create Vendor'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorQuickAddDialog;
